// Turns any Marian feast from the calendar into a printable prayer card (and booklet item),
// with a fitting sacred image, the observance note and a short prayer.
import { MONTHS } from "@/data/calendar";
import { MONTFORT_CARD, isMontfortFeast } from "@/data/montfortCard";
import { SUB_TUUM } from "@/data/historicalPrayers";

const A = "/images/apparition-marian";
const T = "/images/treasury";
const Y = "/images/mysteries";

const IMG = {
  guadalupe: { src: `${T}/guadalupe.webp`, alt: "Our Lady of Guadalupe", credit: "Our Lady of Guadalupe · the tilma image · Public domain" },
  lourdes: { src: `${A}/lourdes.webp`, alt: "The Immaculate Conception", credit: "The Immaculate Conception · Murillo · Public domain" },
  rosary: { src: `${T}/our-lady-rosary.webp`, alt: "Our Lady of the Rosary", credit: "Our Lady of the Rosary of Pompeii · Public domain" },
  carmel: { src: `${T}/mount-carmel.webp`, alt: "Our Lady of Mount Carmel", credit: "Our Lady of Mount Carmel · Public domain" },
  sorrows: { src: `${A}/salette.webp`, alt: "The sorrowful Virgin", credit: "Mater Dolorosa · Titian · Public domain" },
  heart: { src: `${A}/beauraing.webp`, alt: "The Immaculate Heart of Mary", credit: "Immaculate Heart of Mary · Diana Ringo · CC BY-SA 4.0" },
  annunciation: { src: `${Y}/joyful-1.webp`, alt: "The Annunciation", credit: "The Annunciation · Fra Filippo Lippi · Public domain" },
  visitation: { src: `${Y}/joyful-2.webp`, alt: "The Visitation", credit: "The Visitation · Philippe de Champaigne · Public domain" },
  coronation: { src: `${Y}/glorious-5.webp`, alt: "The Coronation of the Virgin", credit: "The Coronation of the Virgin · Annibale Carracci · Public domain" },
  motherChild: { src: `${A}/siluva.webp`, alt: "Madonna and Child", credit: "Madonna and Child · Carlo Dolci · Public domain" },
  perpetual: { src: `${T}/perpetual-help.webp`, alt: "Our Lady of Perpetual Help", credit: "Our Lady of Perpetual Help · the Cretan icon · Public domain" },
  medal: { src: `${T}/miraculous-medal.webp`, alt: "The Miraculous Medal", credit: "Medal of the Immaculate Conception · CC BY-SA 3.0" },
  czestochowa: { src: `${T}/czestochowa.webp`, alt: "Our Lady of Częstochowa", credit: "Our Lady of Częstochowa · the Black Madonna · Public domain" },
  vladimir: { src: `${T}/vladimir.webp`, alt: "The Theotokos of Vladimir", credit: "The Virgin of Vladimir · 12th-century icon · Public domain" },
  sign: { src: `${T}/sign.webp`, alt: "Our Lady of the Sign", credit: "Our Lady of the Sign · Novgorod · Public domain" },
  mariahilf: { src: `${A}/filipov.webp`, alt: "Our Lady, Help of Christians", credit: "Mariahilf · Lucas Cranach the Elder · Public domain" },
  angels: { src: `${T}/virgin-angels.webp`, alt: "The Virgin with Angels", credit: "The Virgin with Angels · W.-A. Bouguereau · Public domain" },
  filipov: { src: `${A}/filipov.webp`, alt: "Our Lady of Filipov", credit: "Mariahilf · Lucas Cranach the Elder · Public domain — Our Lady, Help of Christians" },
  banneux: { src: `${T}/virgin-angels.webp`, alt: "The Virgin of the Poor", credit: "The Virgin with Angels · Bouguereau · Public domain — the Virgin of the Poor" },
  pontmain: { src: `${A}/pontmain.webp`, alt: "Our Lady of Hope of Pontmain", credit: "The Immaculate Conception · Murillo · Public domain" },
  zion: { src: `${T}/miraculous-medal.webp`, alt: "The Madonna of the Miracle", credit: "The Miraculous Medal image · CC BY-SA 3.0" },
  laus: { src: `${A}/laus.webp`, alt: "Our Lady of Laus, Refuge of Sinners", credit: "The Madonna in Prayer · Sassoferrato · Public domain" },
  knock: { src: `${Y}/glorious-5.webp`, alt: "Our Lady, Queen of Ireland", credit: "The Coronation of the Virgin · Carracci · Public domain — Our Lady, Queen of Ireland" },
  beauraing: { src: `${A}/beauraing.webp`, alt: "The Virgin with the Golden Heart", credit: "Immaculate Heart of Mary · Diana Ringo · CC BY-SA 4.0" },
  kibeho: { src: `${A}/kibeho.webp`, alt: "Our Lady of Kibeho, Mother of the Word", credit: "Madonna and Child with Angels · Sassoferrato · Public domain" },
  salette: { src: `${A}/salette.webp`, alt: "Our Lady of La Salette", credit: "Mater Dolorosa · Titian · Public domain" },
  siluva: { src: `${A}/siluva.webp`, alt: "Our Lady of Šiluva", credit: "Madonna and Child · Carlo Dolci · Public domain" },
  gietrzwald: { src: `${A}/gietrzwald.webp`, alt: "Our Lady of Gietrzwałd", credit: "The Immaculate Conception · Murillo · Public domain" },
  lezajsk: { src: `${A}/lezajsk.webp`, alt: "Our Lady of Leżajsk", credit: "The Virgin in Prayer · Sassoferrato · Public domain" },
  fatima: { src: `${A}/fatima.webp`, alt: "Our Lady of Fátima with the shepherd children", credit: "Our Lady of Fátima with the shepherd children · devotional image provided by the site owner" },
};

// Ordered keyword rules — first match wins.
const RULES = [
  [/guadalupe/i, IMG.guadalupe],
  [/perpetual help/i, IMG.perpetual],
  [/miraculous medal|médaille|rue du bac/i, IMG.medal],
  [/częstochowa|czestochowa|jasna g/i, IMG.czestochowa],
  [/mount carmel|carmel|scapular|flos carmeli/i, IMG.carmel],
  [/rosary|rosaire|pompei|lepanto/i, IMG.rosary],
  [/immaculate heart/i, IMG.heart],
  [/immaculate conception|immacul/i, IMG.lourdes],
  [/annunciation/i, IMG.annunciation],
  [/visitation/i, IMG.visitation],
  [/assumption|dormition|falling.?asleep|koimesis/i, IMG.coronation],
  [/queenship|queen of|regina/i, IMG.coronation],
  [/nativity of|birth of|conception of the theotokos|conception by st/i, IMG.motherChild],
  [/presentation of mary|entrance of the theotokos|presentation of the/i, IMG.sign],
  [/sorrows|dolou?rs|stabat|pietà|pieta|compassion/i, IMG.sorrows],
  [/help of christians|auxilium|perpetuo|mariahilf/i, IMG.mariahilf],
  [/protection|pokrov|veil|robe of the|cincture|belt|deposition/i, IMG.sign],
  [/akathist|life-?giving spring|zoodochos/i, IMG.sign],
  [/perpetual help|hodegetria/i, IMG.perpetual],
  [/lourdes/i, IMG.lourdes],
  [/f[áa]tima/i, IMG.fatima],
  [/filipov/i, IMG.filipov],
  [/banneux|virgin of the poor/i, IMG.banneux],
  [/pontmain/i, IMG.pontmain],
  [/zion|miracolo|ratisbonne/i, IMG.zion],
  [/laus/i, IMG.laus],
  [/knock/i, IMG.knock],
  [/beauraing/i, IMG.beauraing],
  [/kibeho/i, IMG.kibeho],
  [/salette/i, IMG.salette],
  [/[ŠS]iluva|siluva/i, IMG.siluva],
  [/gietrzwa/i, IMG.gietrzwald],
  [/leżajsk|lezajsk/i, IMG.lezajsk],
  [/theotokos|mother of god|divine motherhood|mother of the church|god.?bearer|synaxis/i, IMG.vladimir],
  [/name of mary|holy name/i, IMG.rosary],
];

export const feastImage = (name = "") => {
  for (const [re, img] of RULES) if (re.test(name)) return img;
  return IMG.angels;
};

const shortTitle = (name = "") => name.split(/[—:·]/)[0].trim();

const prayerText = () => SUB_TUUM.text;

const dateLabel = (f) => `${f.day} ${MONTHS[(f.month || 1) - 1]}`;
const sourceUrl = (s) => (!s ? undefined : /^https?:\/\//.test(s) ? s : `${window.location.origin}${s}`);

export const feastToCard = (f) => isMontfortFeast(f) ? MONTFORT_CARD : ({
  eyebrow: `${dateLabel(f)} · ${f.rank || "Observance"}`,
  title: f.name,
  subtitle: f.rite,
  image: feastImage(f.name),
  blocks: [
    ...(f.detail || f.note ? [{ label: "The observance", text: f.detail || f.note }] : []),
    { label: SUB_TUUM.title, text: prayerText(f.name), italic: true },
    { label: "Prayer edition", text: `${SUB_TUUM.sourceLabel}\n${SUB_TUUM.sourceUrl}` },
  ],
  note: f.original_name && f.original_name !== f.name ? `Proper title: ${f.original_name}.` : undefined,
  source: f.source ? { label: "Source & editorial standards", url: sourceUrl(f.source) } : undefined,
  back: {
    eyebrow: dateLabel(f),
    name: shortTitle(f.name),
    blessing: SUB_TUUM.latin,
  },
  footer: "Theotokos · Mater Dei — Ad Iesum per Mariam",
});

export const feastToBookletItem = (f) => isMontfortFeast(f) ? { ...MONTFORT_CARD, tag: "Optional memorial" } : ({
  title: f.name,
  subtitle: `${dateLabel(f)} · ${f.rite} · ${f.rank || "Observance"}`,
  tag: f.category === "marian" ? "Marian feast" : f.rank,
  image: feastImage(f.name),
  blocks: [
    ...(f.detail || f.note ? [{ label: "The observance", text: f.detail || f.note }] : []),
    { label: SUB_TUUM.title, text: prayerText(f.name), italic: true },
    { label: "Prayer edition", text: `${SUB_TUUM.sourceLabel}\n${SUB_TUUM.sourceUrl}` },
  ],
  note: f.original_name && f.original_name !== f.name ? `Proper title: ${f.original_name}.` : undefined,
  source: f.source ? { label: "Source", url: sourceUrl(f.source) } : undefined,
});

// De-duplicate Marian feasts by name, keeping the earliest date, and order by date.
export const uniqueMarianFeasts = (records = []) => {
  const seen = new Map();
  for (const f of records) {
    if (f.category !== "marian") continue;
    const key = (f.name || "").trim().toLowerCase();
    if (!seen.has(key)) seen.set(key, f);
  }
  return [...seen.values()].sort((a, b) => (a.month - b.month) || (a.day - b.day));
};
